// ProductFilterDropdown component - Filter by THC/CBD amounts and menu type
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

const ProductFilterDropdown = ({ filters, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const filterOptions = {
    stockFilter: [
      { value: 'inStockOnly', label: 'In Stock Only' }
    ],
    thcAmount: [
      { value: '150', label: 'THC 150 MG' },
      { value: '300', label: 'THC 300 MG' },
      { value: '500', label: 'THC 500 MG' },
      { value: '1000', label: 'THC 1000 MG' }
    ],
    cbdAmount: [
      { value: '300', label: 'CBD 300 MG' },
      { value: '500', label: 'CBD 500 MG' }
    ],
    menuType: [
      { value: 'rec', label: 'Recreational' },
      { value: 'medical', label: 'Medical' }
    ]
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleFilterChange = (filterType, value) => {
    const newFilters = { ...filters };
    
    if (filterType === 'thcAmount' || filterType === 'cbdAmount') {
      // Toggle the filter value
      if (newFilters[filterType].includes(value)) {
        newFilters[filterType] = newFilters[filterType].filter(v => v !== value);
      } else {
        newFilters[filterType] = [...newFilters[filterType], value];
      }
    } else if (filterType === 'menuType') {
      // Toggle the menu type
      if (newFilters.menuType.includes(value)) {
        newFilters.menuType = newFilters.menuType.filter(v => v !== value);
      } else {
        newFilters.menuType = [...newFilters.menuType, value];
      }
    } else if (filterType === 'stockFilter') {
      // Toggle the stock filter
      if (newFilters.stockFilter.includes(value)) {
        newFilters.stockFilter = newFilters.stockFilter.filter(v => v !== value);
      } else {
        newFilters.stockFilter = [...newFilters.stockFilter, value];
      }
    }
    
    onChange(newFilters);
  };

  const clearAllFilters = () => {
    onChange({
      stockFilter: [],
      thcAmount: [],
      cbdAmount: [],
      menuType: []
    });
  };

  const hasActiveFilters = filters.thcAmount.length > 0 || filters.cbdAmount.length > 0 || filters.menuType.length > 0 || filters.stockFilter.length > 0;
  const activeFilterCount = filters.thcAmount.length + filters.cbdAmount.length + filters.menuType.length + filters.stockFilter.length;

  return (
    <div className="filter-dropdown-container" ref={dropdownRef}>
      <button
        type="button"
        className={`filter-dropdown-toggle ${isOpen ? 'open' : ''} ${hasActiveFilters ? 'has-filters' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className="dropdown-text">
          Filters
          {hasActiveFilters && <span className="filter-count">({activeFilterCount})</span>}
        </span>
        <span className="dropdown-arrow">▼</span>
      </button>

      {isOpen && (
        <div className="filter-dropdown-menu" role="listbox">
          <div className="dropdown-header">
            {/* <span>Filter Products</span> */}
            {hasActiveFilters && (
              <button className="clear-filters-btn" onClick={clearAllFilters}>
                Clear All
              </button>
            )}
          </div>
          
          <div className="dropdown-content">
            {/* Stock Filter Section */}
            <div className="filter-category">
              <div className="category-header">Availability</div>
              <div className="filter-options">
                {filterOptions.stockFilter.map((option) => (
                  <div
                    key={`stock-${option.value}`}
                    className={`filter-option ${filters.stockFilter.includes(option.value) ? 'selected' : ''}`}
                    onClick={() => handleFilterChange('stockFilter', option.value)}
                  >
                    <span className="option-label">{option.label}</span>
                    {filters.stockFilter.includes(option.value) && <span className="selected-indicator">✓</span>}
                  </div>
                ))}
              </div>
            </div>

            {/* Menu Type Section */}
            <div className="filter-category">
              <div className="category-header">Menu Type</div>
              <div className="filter-options">
                {filterOptions.menuType.map((option) => (
                  <div
                    key={`menu-${option.value}`}
                    className={`filter-option ${filters.menuType.includes(option.value) ? 'selected' : ''}`}
                    onClick={() => handleFilterChange('menuType', option.value)}
                  >
                    <span className="option-label">{option.label}</span>
                    {filters.menuType.includes(option.value) && <span className="selected-indicator">✓</span>}
                  </div>
                ))}
              </div>
            </div>

            {/* THC Amount Section */}
            <div className="filter-category">
              <div className="category-header">THC Amount</div>
              <div className="filter-options">
                {filterOptions.thcAmount.map((option) => (
                  <div
                    key={`thc-${option.value}`}
                    className={`filter-option ${filters.thcAmount.includes(option.value) ? 'selected' : ''}`}
                    onClick={() => handleFilterChange('thcAmount', option.value)}
                  >
                    <span className="option-label">{option.label}</span>
                    {filters.thcAmount.includes(option.value) && <span className="selected-indicator">✓</span>}
                  </div>
                ))}
              </div>
            </div>

            {/* CBD Amount Section */}
            <div className="filter-category">
              <div className="category-header">CBD Amount</div>
              <div className="filter-options">
                {filterOptions.cbdAmount.map((option) => (
                  <div
                    key={`cbd-${option.value}`}
                    className={`filter-option ${filters.cbdAmount.includes(option.value) ? 'selected' : ''}`}
                    onClick={() => handleFilterChange('cbdAmount', option.value)}
                  >
                    <span className="option-label">{option.label}</span>
                    {filters.cbdAmount.includes(option.value) && <span className="selected-indicator">✓</span>}
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

ProductFilterDropdown.propTypes = {
  filters: PropTypes.shape({
    thcAmount: PropTypes.array.isRequired,
    cbdAmount: PropTypes.array.isRequired,
    menuType: PropTypes.array.isRequired,
    stockFilter: PropTypes.array.isRequired
  }).isRequired,
  onChange: PropTypes.func.isRequired
};

export default ProductFilterDropdown;
